import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, catchError, finalize, first, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { SalesOrderChange } from 'src/app/core/models/sales/sales-order-change.interface';
import { SalesOrderChangeDiff } from 'src/app/core/models/sales/sales-order-change-diff.interface';
import { SalesOrderService } from 'src/app/core/services/sales-order.service';
import {
  SalesOrderHistoryDiffDisplayValueVm,
  SalesOrderHistoryDiffRowVm,
  SalesOrderHistoryEntryVariant,
  SalesOrderHistoryEntryVm,
} from './sales-order-history-tab.view-model';

@Component({
  selector: 'app-sales-order-history-tab',
  standalone: true,
  imports: [CommonModule, CoreModule, TranslateModule],
  templateUrl: './sales-order-history-tab.component.html',
  styleUrl: './sales-order-history-tab.component.scss',
})
export class SalesOrderHistoryTabComponent implements OnChanges {
  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public salesOrderId = '';
  // Parent increments this counter whenever the underlying order is mutated so we know to refetch.
  @Input() public refreshToken = 0;
  // Gates the fetch on whether the History tab is the currently active tab. The parent overview
  // bumps `refreshToken` on EVERY applySalesOrder (initial load, shipment-created, invoice-created),
  // so without this gate we'd fire GET /changes even when the user never opens the tab. When
  // `isActive` flips to true we eagerly fetch; while it's true we react to subsequent refreshToken
  // bumps; while it's false we skip every cycle.
  @Input() public isActive = false;

  private readonly salesOrderService = inject(SalesOrderService);

  public isLoading = false;
  public errorKey = '';
  public historyEntries: SalesOrderHistoryEntryVm[] = [];

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['companyId']
      && !changes['salesOrderId']
      && !changes['refreshToken']
      && !changes['isActive']
    ) {
      return;
    }
    if (!this.companyId || !this.salesOrderId) {
      this.historyEntries = [];
      return;
    }
    if (!this.isActive) {
      // Tab not visible — don't waste a request. We'll fetch the moment isActive flips true.
      return;
    }
    const isActiveChange = changes['isActive'];
    const justBecameActive =
      !!isActiveChange && isActiveChange.currentValue && !isActiveChange.previousValue;
    // Cases we want to trigger a fetch:
    //   - tab just opened (isActive false -> true),
    //   - tab already open and either companyId / salesOrderId / refreshToken changed.
    // If isActive itself didn't change AND none of the data inputs changed, there's nothing to do.
    if (
      !justBecameActive
      && !changes['companyId']
      && !changes['salesOrderId']
      && !changes['refreshToken']
    ) {
      return;
    }
    this.loadHistory();
  }

  private loadHistory(): void {
    this.isLoading = true;
    this.errorKey = '';
    this.salesOrderService
      .getSalesOrderChanges(this.companyId, this.salesOrderId)
      .pipe(
        first(),
        tap((changes) => {
          this.historyEntries = changes.map((change) => this.buildEntry(change));
        }),
        catchError(() => {
          this.errorKey = 'COMPONENTS.SALES_ORDERS.HISTORY.ERROR';
          this.historyEntries = [];
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private buildEntry(change: SalesOrderChange): SalesOrderHistoryEntryVm {
    const diffRows = change.changes
      .map((diff) => this.buildDiffRow(change.id, diff))
      .filter((row): row is SalesOrderHistoryDiffRowVm => row !== null);

    const variant = this.resolveEntryVariant(change);

    return {
      id: change.id,
      summaryKey: this.resolveSummaryKey(change),
      initiator: change.initiator,
      createdAt: change.createdAt,
      variant,
      dotClass: this.resolveEntryDotClass(variant),
      // Entity-lifecycle rows (Created / Duplicated) carry no diff payload — drop the
      // diff list entirely so the template doesn't render an empty body section.
      diffRows: this.isEntityLifecycleEvent(change.eventType) ? [] : diffRows,
    };
  }

  private isEntityLifecycleEvent(eventType: SalesOrderChange['eventType']): boolean {
    return eventType === 'Created' || eventType === 'Duplicated';
  }

  private buildDiffRow(changeId: string, diff: SalesOrderChangeDiff): SalesOrderHistoryDiffRowVm | null {
    const labelKey = this.resolveFieldLabelKey(diff.path);
    if (!labelKey) {
      return null;
    }
    return {
      id: `${changeId}:${diff.path}`,
      labelKey,
      ...this.buildDiffRowValueVm(diff.path, diff.oldValue, diff.newValue),
    };
  }

  private resolveEntryDotClass(variant: SalesOrderHistoryEntryVariant): string {
    const dotClasses: Record<SalesOrderHistoryEntryVariant, string> = {
      archived: 'sales-order-history-tab__item-dot--archived',
      restored: 'sales-order-history-tab__item-dot--restored',
      updated: 'sales-order-history-tab__item-dot--updated',
      cancelled: 'sales-order-history-tab__item-dot--cancelled',
      created: 'sales-order-history-tab__item-dot--created',
    };

    return dotClasses[variant];
  }

  private resolveSummaryKey(change: SalesOrderChange): string {
    // Branch on the backend EventType discriminator first so entity-lifecycle rows
    // (Created / Duplicated) render dedicated messages without walking the diff. Legacy
    // rows persisted before the 2026-05-20 migration carry eventType = null and fall
    // through to the existing diff-based resolver, which keeps their output unchanged.
    if (change.eventType === 'Created') {
      return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.CREATED';
    }
    if (change.eventType === 'Duplicated') {
      return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.DUPLICATED';
    }

    const archiveChange = change.changes.find((entry) => this.normalizePath(entry.path) === 'isarchived');
    if (archiveChange) {
      return archiveChange.newValue === true
        ? 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.ARCHIVED'
        : 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.UNARCHIVED';
    }

    const statusChange = change.changes.find((entry) => this.normalizePath(entry.path) === 'status');
    if (statusChange) {
      return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.STATUS';
    }

    // Per spec: any diff under "lines/..." (root add/remove at "lines/<guid>" or sub-field
    // updates at "lines/<guid>/<field>") classifies the entry as a line change. Whole-line
    // add/remove is distinguished by the line-root diff having undefined old/new values.
    const lineChanges = change.changes.filter((entry) => this.normalizePath(entry.path).startsWith('lines/'));
    if (lineChanges.length) {
      const rootLineDiff = lineChanges.find((entry) => this.isLineRootDiff(entry.path));
      if (rootLineDiff) {
        if (rootLineDiff.oldValue === undefined) {
          return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.LINE_ADDED';
        }
        if (rootLineDiff.newValue === undefined) {
          return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.LINE_REMOVED';
        }
      }
      return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.LINE_UPDATED';
    }

    const rootKeys = change.changes.map((entry) => this.normalizePath(entry.path).split('/')[0]);
    if (rootKeys.includes('requesteddeliverydate')) {
      return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.DELIVERY_DATE';
    }
    if (rootKeys.includes('shortdescription')) {
      return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.DESCRIPTION';
    }

    return 'COMPONENTS.SALES_ORDERS.HISTORY.EVENTS.DETAILS';
  }

  private resolveEntryVariant(change: SalesOrderChange): SalesOrderHistoryEntryVariant {
    // Entity-lifecycle rows render the "created" timeline marker so the dot color
    // matches the dedicated Order created / Order duplicated message.
    if (change.eventType === 'Created' || change.eventType === 'Duplicated') {
      return 'created';
    }

    const archiveChange = change.changes.find((entry) => this.normalizePath(entry.path) === 'isarchived');
    if (archiveChange) {
      return archiveChange.newValue === true ? 'archived' : 'restored';
    }

    const statusChange = change.changes.find((entry) => this.normalizePath(entry.path) === 'status');
    if (statusChange) {
      if (statusChange.newValue === 'Cancelled') {
        return 'cancelled';
      }
      if (statusChange.newValue === 'New') {
        return 'created';
      }
    }

    return 'updated';
  }

  private resolveFieldLabelKey(path: string): string | null {
    const normalizedPath = path.replace(/^\//, '');
    const segments = normalizedPath.split('/');
    const isLineField = segments[0] === 'lines' && segments.length >= 3;
    const fieldKey = isLineField ? segments[2] : segments[0];

    const headerLabels: Record<string, string> = {
      number: 'COMPONENTS.SALES_ORDERS.FIELDS.NUMBER',
      customerNumber: 'COMPONENTS.SALES_ORDERS.FIELDS.CUSTOMER_NUMBER',
      customerDisplayName: 'COMPONENTS.SALES_ORDERS.FIELDS.CUSTOMER',
      shortDescription: 'COMPONENTS.SALES_ORDERS.FIELDS.SHORT_DESCRIPTION',
      status: 'COMPONENTS.SALES_ORDERS.FIELDS.STATUS',
      requestedDeliveryDate: 'COMPONENTS.SALES_ORDERS.FIELDS.REQUESTED_DELIVERY_DATE',
      isArchived: 'COMPONENTS.SALES_ORDERS.FIELDS.IS_ARCHIVED',
    };

    const lineLabels: Record<string, string> = {
      sequence: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.SEQUENCE',
      inventoryItemDisplayName: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.ITEM',
      description: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.DESCRIPTION',
      description2: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.DESCRIPTION_2',
      quantity: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.QUANTITY',
      unitOfMeasureCode: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.UNIT',
      unitPrice: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.UNIT_PRICE',
      taxPercent: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.TAX_PERCENT',
      amountIncludingTax: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.AMOUNT_INCLUDING_TAX',
      shippedQuantity: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.SHIPPED_QUANTITY',
      invoicedQuantity: 'COMPONENTS.SALES_ORDERS.LINE_FIELDS.INVOICED_QUANTITY',
    };

    if (fieldKey === 'id' || fieldKey === 'inventoryItemId') {
      return null;
    }

    return (isLineField ? lineLabels[fieldKey] : headerLabels[fieldKey]) ?? null;
  }

  private buildDiffRowValueVm(
    path: string,
    oldValue: unknown,
    newValue: unknown,
  ): Pick<SalesOrderHistoryDiffRowVm, 'showOldValue' | 'oldValue' | 'newValue'> {
    const formattedOldValue = this.formatDiffValue(path, oldValue);
    const formattedNewValue = this.formatDiffValue(path, newValue);
    const showOldValue = oldValue !== null && oldValue !== undefined && oldValue !== '';

    return {
      showOldValue,
      oldValue: showOldValue ? formattedOldValue : undefined,
      newValue: formattedNewValue,
    };
  }

  private formatDiffValue(path: string, value: unknown): SalesOrderHistoryDiffDisplayValueVm {
    if (value === null || value === undefined || value === '') {
      return { text: 'COMMON.EMPTY_VALUE', translationKey: 'COMMON.EMPTY_VALUE' };
    }

    if (typeof value === 'boolean') {
      const key = value ? 'COMMON.YES' : 'COMMON.NO';
      return { text: key, translationKey: key };
    }

    if (this.isStatusPath(path) && typeof value === 'string') {
      const translationKey = `COMPONENTS.SALES_ORDERS.STATUSES.${value.toUpperCase()}`;
      return { text: translationKey, translationKey };
    }

    return { text: `${value}` };
  }

  private isStatusPath(path: string): boolean {
    return this.normalizePath(path) === 'status';
  }

  private isLineRootDiff(path: string): boolean {
    const normalized = this.normalizePath(path);
    if (!normalized.startsWith('lines/')) {
      return false;
    }
    // Whole-line add/remove diffs are emitted at "lines/<guid>" exactly (depth 2);
    // sub-field diffs go deeper, e.g. "lines/<guid>/quantity".
    return normalized.split('/').length === 2;
  }

  private normalizePath(path: string): string {
    return path.replace(/^\//, '').toLowerCase();
  }
}
